import { useEffect } from 'react';

/**
 * @param {object} params
 * @param {boolean} params.isFetching
 * @param {boolean | undefined} params.hasNextPage
 * @param {() => void} params.onShouldFetch
 * @param {number} [params.threshold]
 */

export interface UseInfiniteScrollProps {
	isFetching: boolean;
	hasNextPage: boolean | undefined;
	onShouldFetch: () => void;
	threshold?: number;
}

const useInfiniteScroll = ({
	isFetching,
	hasNextPage,
	onShouldFetch,
	threshold = 100,
}: UseInfiniteScrollProps) => {
	// continue to fetch more items if at bottom of page
	useEffect(() => {
		const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
		const isAtBottom = scrollHeight - scrollTop - clientHeight <= 0;

		if (isAtBottom && !isFetching && hasNextPage) {
			onShouldFetch();
		}
	}, [isFetching, hasNextPage, onShouldFetch]);

	// fetch once scroll meets threshold
	useEffect(() => {
		function handleScrollChange() {
			const { scrollHeight, scrollTop, clientHeight } =
				document.documentElement;

			if (isFetching || !hasNextPage) {
				return;
			}

			const meetsThreshold =
				scrollHeight - scrollTop - clientHeight < threshold;

			if (meetsThreshold) {
				onShouldFetch();
			}
		}

		document.addEventListener('scroll', handleScrollChange);
		return () => {
			document.removeEventListener('scroll', handleScrollChange);
		};
	}, [isFetching, hasNextPage, onShouldFetch, threshold]);
};

export default useInfiniteScroll;
